import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';
import {ClientCustomFieldDefinition} from '../../../types';
import {configKeys} from '../settings-client-custom.config-keys';


/**
 * Service for client custom field definitions API calls.
 */
@Injectable()
export class SettingsClientCustomService {
  /**
   * Event emitter for when a definition was saved.
   */
  private definitionSavedEvent: EventEmitter<any> = new EventEmitter();

  public constructor(
    private registryService: RegistryService,
    private configService: ConfigService,
    private http: HttpClient
  ) {
  }

  /**
   * Loads all custom field definitions.
   *
   * @param filter
   * @param opts
   */
  public loadCustomFields(
    filter?: ClientCustomFieldDefinition.Filter,
    opts?: ClientCustomFieldDefinition.Opts
  ): Promise<{rows: ClientCustomFieldDefinition[], total: number}> {
    const query: any = {filter: JSON.stringify(filter) || '{}', opts: JSON.stringify(opts) || '{}'};
    const basePath = `${this.getApiUrl()}?${decodeURI(new URLSearchParams(query).toString())}`;

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe((response: any) => {
            const rows = response.definitions || response.customFields || response.rows || [];
            const total = response.total || 0;
            resolve({rows, total});
          },
          error => {
            reject(error);
          });
    });
  }

  /**
   * Loads a single custom field definition by uid.
   *
   * @param uid
   */
  public loadCustomField(uid: string): Promise<ClientCustomFieldDefinition> {
    const basePath = this.getApiUrl(`/${uid}`);

    return new Promise((resolve, reject) => {
      this.http.get(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => resolve(response.definition || response.customField || response || null),
          error => reject(error)
        );
    });
  }

  /**
   * Creates a new custom field definition.
   *
   * @param definition
   */
  public createCustomField(definition: ClientCustomFieldDefinition): Promise<ClientCustomFieldDefinition> {
    const basePath = this.getApiUrl();

    return new Promise((resolve, reject) => {
      this.http.post(basePath, definition, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => resolve(response.definition || response.customField || response),
          error => reject(error)
        );
    });
  }

  /**
   * Updates an existing custom field definition.
   *
   * @param definition
   */
  public updateCustomField(definition: ClientCustomFieldDefinition): Promise<ClientCustomFieldDefinition> {
    const basePath = this.getApiUrl(`/${definition.uid}`);

    return new Promise((resolve, reject) => {
      this.http.put(basePath, definition, {headers: this.getAuthHeaders()})
        .subscribe(
          (response: any) => resolve(response.definition || response.customField || response),
          error => reject(error)
        );
    });
  }

  /**
   * Deletes a custom field definition.
   *
   * @param definition
   */
  public deleteCustomField(definition: ClientCustomFieldDefinition): Promise<void> {
    const basePath = this.getApiUrl(`/${definition.uid}`);

    return new Promise<void>((resolve, reject) => {
      this.http.delete(basePath, {headers: this.getAuthHeaders()})
        .subscribe(
          () => resolve(),
          error => reject(error)
        );
    });
  }

  /**
   * Returns event emitter for when a definition was saved.
   */
  public onDefinitionSaved(): EventEmitter<any> {
    return this.definitionSavedEvent;
  }

  /**
   * Returns api url for client custom fields.
   *
   * @param suffix
   */
  private getApiUrl(suffix: string = ''): string {
    const uri = this.configService.get(configKeys.SETTINGS_URI_CONFIG_KEY);
    return `${uri}/client-custom${suffix}`;
  }

  /**
   * Returns auth headers by auth token and tenant id.
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get(configKeys.SESSION_AUTH_TOKEN);
    const tenantId = this.registryService.get(configKeys.SESSION_TENANT_ID);

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
